<template>
  <div class="tw:h-full tw:flex tw:flex-col">
    <EdroneLoading v-if="isHistoryLoading" class="tw:h-full tw:items-center tw:justify-center tw:m-auto" />

    <template v-else>
      <!-- Main content area with centered form (hidden when chat is active) -->
      <div
        v-if="chatMessages.length === 0"
        class="tw:flex-1 tw:flex tw:flex-col tw:items-center tw:justify-center tw:p-6"
      >
        <div class="tw:w-full tw:max-w-md tw:flex tw:flex-col tw:gap-6">
          <div class="tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-2">
            <h1 class="tw:text-xl tw:font-bold tw:text-black tw:text-center">
              {{ trans.greeting.good_morning }},
              <span class="tw:capitalize">{{ EDRONE_CONTEXT.user.firstName }}</span>
              !
            </h1>
            <p class="tw:text-sm tw:text-default-700 tw:text-center">
              {{ trans.greeting.ready_to_create }}
            </p>
          </div>

          <!-- Attachment preview (above textarea) -->
          <div v-if="preview" class="tw:relative tw:inline-block tw:w-fit tw:mx-auto">
            <img
              :src="preview"
              alt="Attachment preview"
              class="tw:max-h-32 tw:rounded-lg tw:border tw:border-default-200"
            />
            <button
              v-if="!isGenerating"
              class="tw:absolute tw:-top-2 tw:-right-2 tw:bg-black tw:text-white tw:rounded-full tw:w-6 tw:h-6 tw:flex tw:items-center tw:justify-center hover:tw:bg-gray-800 tw:transition-colors"
              @click="removeAttachment"
            >
              <FontAwesomeIcon class="tw:text-xs" icon="fa-solid fa-xmark" />
            </button>
          </div>

          <!-- Banner Generation Form -->
          <div class="tw:flex tw:flex-col tw:gap-4">
            <div class="tw:flex tw:flex-col tw:gap-3">
              <EdroneTextarea
                :disabled="isGenerating"
                :focused="true"
                :maxlength="500"
                :placeholder="trans.form.placeholder"
                :rows="4"
                :value="prompt"
                style="font-size: 1rem !important"
                @enter="handleSubmitForm"
                @input="prompt = $event"
              />

              <div class="tw:flex tw:justify-between tw:items-center">
                <div class="tw:flex tw:gap-2">
                  <!-- Prompt History Button -->
                  <div class="tw:relative">
                    <EdroneButton :disabled="isGenerating" type="outline" @click="toggleHistoryDropdown">
                      <template #left-icon>
                        <FontAwesomeIcon icon="fa-light fa-clock-rotate-left" />
                      </template>
                      {{ trans.form.history_button }}
                    </EdroneButton>
                    <div
                      v-if="showHistoryDropdown"
                      class="tw:absolute tw:top-full tw:left-0 tw:mt-1 tw:w-72 tw:bg-white tw:border tw:border-default-200 tw:rounded-lg tw:shadow-lg tw:z-10 tw:max-h-48 tw:overflow-y-auto"
                    >
                      <div>
                        <div
                          v-if="promptHistory.length <= 0"
                          class="tw:w-full tw:px-3 tw:py-2 tw:bg-gray-50 tw:text-sm tw:text-gray-500"
                        >
                          {{ trans.history.no_history }}
                        </div>
                        <div
                          v-for="(item, index) in promptHistory"
                          :key="index"
                          class="tw:w-full tw:cursor-pointer tw:text-left tw:px-3 tw:py-2 tw:text-sm hover:tw:bg-gray-50 tw:border-b tw:border-gray-100 last:tw:border-b-0"
                          @click="selectPromptFromHistory(item)"
                        >
                          <div class="tw:truncate tw:mb-1">{{ item.prompt }}</div>
                          <div class="tw:text-xs tw:text-gray-500">
                            {{ new Date(item.timestamp).toLocaleDateString("pl-PL") }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Attach Image Button -->
                  <EdroneButton
                    v-if="!attachmentPreview && !attachmentUrl"
                    :disabled="isGenerating"
                    type="outline"
                    @click="triggerFileInput"
                  >
                    <template #left-icon>
                      <FontAwesomeIcon icon="fa-light fa-paperclip" />
                    </template>
                    {{ trans.form.attach_button }}
                  </EdroneButton>
                </div>

                <EdroneButton
                  :disabled="prompt.length === 0"
                  :loading="isGenerating"
                  type="ai-solid"
                  @click="handleSubmitForm"
                >
                  {{ trans.form.generate_button }}
                  <template #right-icon>
                    <EdroneAiSparklesIcon />
                  </template>
                </EdroneButton>
              </div>
            </div>

            <!-- Divider section (replacing occasions) -->
            <div class="tw:flex tw:flex-col tw:gap-4 tw:mt-4">
              <div class="tw:flex tw:items-center tw:gap-3">
                <div class="tw:flex-1 tw:h-px tw:bg-default-200"></div>
                <p class="tw:text-xs tw:text-default-600">{{ trans.history.recent_banners }}</p>
                <div class="tw:flex-1 tw:h-px tw:bg-default-200"></div>
              </div>

              <!-- Recent banners (replacing occasions) -->
              <div v-if="history && history.length" class="tw:overflow-x-auto">
                <div class="tw:flex tw:gap-3 tw:justify-start tw:pb-2" style="min-width: fit-content">
                  <div
                    v-for="(item, index) in history"
                    :key="index"
                    class="tw:relative tw:cursor-pointer tw:group tw:flex-shrink-0"
                    @click="handleDownloadBanner(item)"
                  >
                    <img
                      :alt="trans.download.alt_previous"
                      :src="item"
                      class="tw:h-40 tw:w-auto tw:rounded-lg tw:shadow tw:border tw:border-default-200 tw:transition-all tw:group-hover:shadow-md tw:group-hover:scale-105"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Chat response area (shows after generation and replaces main content) -->
      <div v-if="chatMessages.length > 0" class="tw:flex-1 tw:flex tw:flex-col tw:overflow-hidden">
        <!-- Scrollable chat messages area -->
        <div class="tw:flex-1 tw:overflow-y-auto tw:bg-white">
          <div class="tw:p-4">
            <div class="tw:flex tw:flex-col tw:gap-3">
              <AiBannerChatMessage
                v-for="(message, index) in chatMessages"
                :key="index"
                :message="message"
                @iterate="handleIterateFromMessage"
              />

              <!-- Loading message -->
              <AiBannerChatMessage
                v-if="isGenerating || isModifying"
                :message="{
                  type: 'agent',
                  content: isGenerating ? trans.messages.generating : trans.messages.modifying,
                  isLoading: true,
                }"
              />
            </div>
          </div>
        </div>

        <!-- Fixed input section at bottom -->
        <div class="tw:border-t tw:border-default-200 tw:bg-gray-50 tw:p-4 tw:h-[17.5rem]">
          <EdroneTextarea
            :disabled="isGenerating || isModifying"
            :maxlength="500"
            :placeholder="trans.messages.modify_placeholder"
            :rows="4"
            class="tw:w-full tw:mb-3"
            style="font-size: 1rem !important"
            @enter="handleIterate"
            @input="iterationPrompt = $event"
          />
          <div class="tw:flex tw:gap-2 tw:justify-between">
            <EdroneButton :disabled="isGenerating || isModifying" type="outline" @click="handleStartOver">
              <template #left-icon>
                <FontAwesomeIcon icon="fa-solid fa-plus" />
              </template>
              {{ trans.form.start_over_button }}
            </EdroneButton>
            <EdroneButton
              :disabled="!iterationPrompt || !iterationPrompt.trim() || isGenerating || isModifying || !chatId"
              :loading="isModifying"
              type="ai-solid"
              @click="handleIterate"
            >
              <template #left-icon>
                <EdroneAiSparklesIcon />
              </template>
              {{ trans.form.iterate_button }}
            </EdroneButton>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted, onUnmounted } from "vue";
import { useMutation, useQuery } from "@tanstack/vue-query";
import EdroneButton from "edrone-components/EdroneButton.vue";
import EdroneLoading from "edrone-components/EdroneLoading.vue";
import EdroneTextarea from "edrone-components/EdroneTextarea.vue";
import EdroneAiSparklesIcon from "edrone-components/EdroneAiSparklesIcon.vue";
import { generateAiBanner, getHistory, modifyAiBanner } from "vue-app/api/AiBannerService";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { getRandomPrompt, getPromptHistory, savePromptToHistory, type PromptHistoryItem } from "./AiBanner.constants";
import AiBannerChatMessage from "./AiBannerChatMessage.vue";
import { useAiBannerStore } from "vue-app/components/AiBanner/AiBanner.store.ts";
import { storeToRefs } from "pinia";

type ChatMessage = {
  type: "user" | "agent";
  content: string;
  imageUrl?: string;
  isLoading?: boolean;
};

interface Props {
  isOpen: boolean;
}

const props = defineProps<Props>();

const prompt = ref(getRandomPrompt());
const iterationPrompt = ref("");
const selectedModel = ref(2);
const promptHistory = ref<PromptHistoryItem[]>([]);
const showHistoryDropdown = ref(false);
const chatMessages = ref<ChatMessage[]>([]);
const chatId = ref<string | null>(null);
const attachmentFile = ref<File | null>(null);
const attachmentPreview = ref<string | null>(null);
const attachmentUrl = ref<string | null>(null);
const lastUsedPrompt = ref("");
const lastUsedModel = ref(2);
const lastUsedAttachmentUrl = ref<string | null>(null);

const store = useAiBannerStore();
const { hasNewContent } = storeToRefs(store);

const trans = window._trans._branding.aiBanner;

const preview = computed(() => (attachmentPreview.value ? attachmentPreview.value : attachmentUrl.value || null));

const { mutate: generate, isPending: isGenerating } = useMutation({
  mutationFn: generateAiBanner,
  onSuccess: (data) => {
    chatMessages.value.push({
      type: "agent",
      content: trans.messages.here_is_banner,
      imageUrl: data.url,
    });
    chatId.value = data.chatId;
    refetchHistory();
    hasNewContent.value = !props.isOpen;
  },
  onError: (error) => {
    window.parent.showToast(trans.errors.generate_failed, "error");
    console.error("Failed to generate banner:", error);
  },
});

const { mutate: modify, isPending: isModifying } = useMutation({
  mutationFn: modifyAiBanner,
  onSuccess: (data) => {
    chatMessages.value.push({
      type: "agent",
      content: trans.messages.here_is_updated,
      imageUrl: data.url,
    });
    chatId.value = data.chatId;
    iterationPrompt.value = "";
    refetchHistory();
    hasNewContent.value = !props.isOpen;
  },
  onError: (error) => {
    window.parent.showToast(trans.errors.modify_failed, "error");
    console.error("Failed to modify banner:", error);
  },
});

const {
  data: history,
  refetch: refetchHistory,
  isLoading: isHistoryLoading,
} = useQuery<Array<string>>({
  queryKey: ["bannersHistory"],
  queryFn: async () => {
    return (await getHistory()) || [];
  },
});
refetchHistory();

const handleSubmitForm = () => {
  if (!prompt.value.trim()) return;
  chatMessages.value.push({
    type: "user",
    content: prompt.value,
    imageUrl: preview.value || undefined,
  });

  lastUsedPrompt.value = prompt.value;
  lastUsedModel.value = selectedModel.value;
  lastUsedAttachmentUrl.value = attachmentUrl.value;
  savePromptToHistory(prompt.value);
  loadPromptHistory();
  chatId.value = null;

  generate({
    prompt: prompt.value,
    modelId: selectedModel.value,
    attachment: attachmentFile.value,
    attachmentUrl: attachmentUrl.value,
  });

  prompt.value = "";
};

const handleStartOver = () => {
  chatMessages.value = [];
  attachmentFile.value = null;
  attachmentPreview.value = null;
  attachmentUrl.value = null;
  selectedModel.value = 2;
  iterationPrompt.value = "";
  chatId.value = null;
  prompt.value = getRandomPrompt();
};

const handleIterate = () => {
  if (!iterationPrompt.value.trim() || !chatId.value) return;

  chatMessages.value.push({
    type: "user",
    content: iterationPrompt.value,
  });

  modify({
    prompt: iterationPrompt.value,
    chatId: chatId.value,
  });
};

const handleIterateFromMessage = (message: string) => {
  iterationPrompt.value = message;
  handleIterate();
};

const triggerFileInput = () => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";
  input.onchange = handleFileSelect;
  input.click();
};

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (file) {
    processFile(file);
  }
};

const processFile = (file: File) => {
  if (!file.type.startsWith("image/")) {
    window.parent.showToast(trans.errors.invalid_file, "error");
    return;
  }

  const maxSize = 5 * 1024 * 1024;
  if (file.size > maxSize) {
    window.parent.showToast(trans.errors.file_too_large, "error");
    return;
  }

  attachmentFile.value = file;

  const reader = new FileReader();
  reader.onload = (e) => {
    attachmentPreview.value = e.target?.result as string;
  };
  reader.readAsDataURL(file);
};

const removeAttachment = () => {
  attachmentFile.value = null;
  attachmentPreview.value = null;
  attachmentUrl.value = null;
};

const handleDownloadBanner = (url: string) => {
  downloadImage(url);
};

const downloadImage = (url: string) => {
  const link = document.createElement("a");
  link.href = "/ai-banner/download?url=" + url;
  link.download = `banner-${Date.now()}.png`;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const loadPromptHistory = () => {
  promptHistory.value = getPromptHistory();
};

const selectPromptFromHistory = (historyItem: PromptHistoryItem) => {
  prompt.value = historyItem.prompt;
  showHistoryDropdown.value = false;
};

const toggleHistoryDropdown = () => {
  loadPromptHistory();
  showHistoryDropdown.value = !showHistoryDropdown.value;
};

loadPromptHistory();

const handleClickOutside = (event: MouseEvent) => {
  if (showHistoryDropdown.value) {
    const target = event.target as HTMLElement;
    if (!target.closest(".tw\\:relative")) {
      showHistoryDropdown.value = false;
    }
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>
